/**
 * Per-supplier append-only timber price history for materialsDatabase_timber.
 * Current piecePrice/costPerBdFt stay on the supplierPricing entry; priceHistory
 * records prior points (oldest → newest). Cap at 50 points.
 */

export const TIMBER_PRICE_HISTORY_CAP = 50;

export type TimberPriceHistorySource =
  | 'manual'
  | 'import'
  | 'materials_price_approve'
  | 'seed';

export interface SupplierPricePointLike {
  piecePrice: number;
  costPerBdFt: number;
  recordedAt: Date | string | number | { toDate?: () => Date; seconds?: number };
  source?: TimberPriceHistorySource | string;
}

export interface SupplierPriceWithHistoryLike {
  supplier: string;
  piecePrice: number;
  costPerBdFt: number;
  lastQuoteDate?: unknown;
  notes?: string;
  priceHistory?: SupplierPricePointLike[];
}

const PRICE_EPS = 0.005;

export function pricesDiffer(
  a: { piecePrice: number; costPerBdFt: number },
  b: { piecePrice: number; costPerBdFt: number },
): boolean {
  return (
    Math.abs((a.piecePrice || 0) - (b.piecePrice || 0)) > PRICE_EPS
    || Math.abs((a.costPerBdFt || 0) - (b.costPerBdFt || 0)) > PRICE_EPS
  );
}

export function toHistoryDate(raw: unknown, fallback: Date = new Date()): Date {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return new Date(raw);
  if (typeof raw === 'string') {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (raw && typeof raw === 'object') {
    const o = raw as { toDate?: () => Date; seconds?: number };
    if (typeof o.toDate === 'function') {
      try {
        const d = o.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
      } catch {
        /* ignore */
      }
    }
    if (typeof o.seconds === 'number') return new Date(o.seconds * 1000);
  }
  return fallback;
}

export function normalizePriceHistory(
  raw: unknown,
): Array<{ piecePrice: number; costPerBdFt: number; recordedAt: Date; source?: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ piecePrice: number; costPerBdFt: number; recordedAt: Date; source?: string }> = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const row = p as Record<string, unknown>;
    const piecePrice = Number(row.piecePrice);
    const costPerBdFt = Number(row.costPerBdFt);
    if (!Number.isFinite(piecePrice) || !Number.isFinite(costPerBdFt)) continue;
    const point: {
      piecePrice: number;
      costPerBdFt: number;
      recordedAt: Date;
      source?: string;
    } = {
      piecePrice,
      costPerBdFt,
      recordedAt: toHistoryDate(row.recordedAt),
    };
    if (typeof row.source === 'string' && row.source.trim()) point.source = row.source.trim();
    out.push(point);
  }
  return out;
}

function makePoint(
  piecePrice: number,
  costPerBdFt: number,
  recordedAt: Date,
  source?: TimberPriceHistorySource,
): { piecePrice: number; costPerBdFt: number; recordedAt: Date; source?: TimberPriceHistorySource } {
  const p: {
    piecePrice: number;
    costPerBdFt: number;
    recordedAt: Date;
    source?: TimberPriceHistorySource;
  } = { piecePrice, costPerBdFt, recordedAt };
  if (source) p.source = source;
  return p;
}

/**
 * Apply a new current price onto a supplierPricing entry, appending history when
 * the price changed. New suppliers get a single initial history point.
 */
export function applySupplierPriceWithHistory(
  existing: SupplierPriceWithHistoryLike | null | undefined,
  next: {
    supplier: string;
    piecePrice: number;
    costPerBdFt: number;
    notes?: string;
  },
  opts?: {
    source?: TimberPriceHistorySource;
    recordedAt?: Date;
  },
): SupplierPriceWithHistoryLike {
  const recordedAt = opts?.recordedAt || new Date();
  const source = opts?.source || 'manual';
  const supplier = (next.supplier || existing?.supplier || '').trim();
  const piecePrice = Number.isFinite(next.piecePrice) ? next.piecePrice : 0;
  const costPerBdFt = Number.isFinite(next.costPerBdFt) ? next.costPerBdFt : 0;

  if (!existing) {
    return {
      supplier,
      piecePrice,
      costPerBdFt,
      lastQuoteDate: recordedAt,
      ...(typeof next.notes === 'string' && next.notes.trim()
        ? { notes: next.notes.trim() }
        : {}),
      priceHistory: [makePoint(piecePrice, costPerBdFt, recordedAt, source)],
    };
  }

  const priorHistory = normalizePriceHistory(existing.priceHistory);
  const oldPrices = {
    piecePrice: Number.isFinite(existing.piecePrice) ? existing.piecePrice : 0,
    costPerBdFt: Number.isFinite(existing.costPerBdFt) ? existing.costPerBdFt : 0,
  };
  const newPrices = { piecePrice, costPerBdFt };

  let priceHistory = [...priorHistory];

  if (pricesDiffer(oldPrices, newPrices)) {
    if (priceHistory.length === 0) {
      const seedAt =
        existing.lastQuoteDate != null
          ? toHistoryDate(existing.lastQuoteDate, recordedAt)
          : recordedAt;
      priceHistory.push(makePoint(oldPrices.piecePrice, oldPrices.costPerBdFt, seedAt, 'seed'));
    }
    priceHistory.push(makePoint(piecePrice, costPerBdFt, recordedAt, source));
  } else if (priceHistory.length === 0) {
    // Ensure current price is represented once even when unchanged.
    priceHistory = [makePoint(piecePrice, costPerBdFt, recordedAt, source)];
  }

  if (priceHistory.length > TIMBER_PRICE_HISTORY_CAP) {
    priceHistory = priceHistory.slice(priceHistory.length - TIMBER_PRICE_HISTORY_CAP);
  }

  const out: SupplierPriceWithHistoryLike = {
    supplier: supplier || existing.supplier || '',
    piecePrice,
    costPerBdFt,
    lastQuoteDate: pricesDiffer(oldPrices, newPrices)
      ? recordedAt
      : existing.lastQuoteDate != null
        ? existing.lastQuoteDate
        : recordedAt,
    priceHistory,
  };
  const notes =
    typeof next.notes === 'string'
      ? next.notes.trim()
      : typeof existing.notes === 'string'
        ? existing.notes.trim()
        : '';
  if (notes) out.notes = notes;
  return out;
}

/** Percent change from old → new; null when old is 0. */
export function percentChange(oldValue: number, newValue: number): number | null {
  if (!Number.isFinite(oldValue) || !Number.isFinite(newValue)) return null;
  if (Math.abs(oldValue) < PRICE_EPS) return null;
  return ((newValue - oldValue) / oldValue) * 100;
}

export function priceHistoryPercentChanges(
  history: Array<{ piecePrice: number; costPerBdFt: number; recordedAt: Date; source?: string }>,
): Array<{
  fromIndex: number;
  toIndex: number;
  recordedAt: Date;
  piecePrice: number;
  costPerBdFt: number;
  piecePricePct: number | null;
  costPerBdFtPct: number | null;
}> {
  const points = normalizePriceHistory(history);
  const out: Array<{
    fromIndex: number;
    toIndex: number;
    recordedAt: Date;
    piecePrice: number;
    costPerBdFt: number;
    piecePricePct: number | null;
    costPerBdFtPct: number | null;
  }> = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const cur = points[i]!;
    out.push({
      fromIndex: i - 1,
      toIndex: i,
      recordedAt: cur.recordedAt,
      piecePrice: cur.piecePrice,
      costPerBdFt: cur.costPerBdFt,
      piecePricePct: percentChange(prev.piecePrice, cur.piecePrice),
      costPerBdFtPct: percentChange(prev.costPerBdFt, cur.costPerBdFt),
    });
  }
  return out;
}

/** Last N change segments → up to N+1 points for a sparkline. */
export function lastPriceChangePoints<T extends { piecePrice: number }>(
  history: T[],
  maxChanges = 3,
): T[] {
  if (!history.length) return [];
  const maxPoints = maxChanges + 1;
  if (history.length <= maxPoints) return history;
  return history.slice(history.length - maxPoints);
}

export function priceHistorySummary(history: unknown): {
  pointCount: number;
  lastChangePct: number | null;
  lastRecordedAt: string | null;
} {
  const points = normalizePriceHistory(history);
  if (points.length === 0) {
    return { pointCount: 0, lastChangePct: null, lastRecordedAt: null };
  }
  const last = points[points.length - 1]!;
  let lastChangePct: number | null = null;
  if (points.length >= 2) {
    lastChangePct = percentChange(points[points.length - 2]!.piecePrice, last.piecePrice);
  }
  return {
    pointCount: points.length,
    lastChangePct,
    lastRecordedAt: last.recordedAt.toISOString(),
  };
}
